import { useEffect, useRef, useState } from 'react';
import { Camera, Trash } from '@phosphor-icons/react';

/**
 * Herbruikbaar paneel voor leveringsfoto's (schade/bewijs bij levering).
 * Werkt zowel op de publieke teken-pagina (token) als in de app (auth) via injectie van API-functies.
 */
export default function LeverFotosPanel({ listFotos, uploadFoto, deleteFoto, updateOpmerking }) {
  const [fotos, setFotos] = useState([]);
  const [max, setMax] = useState(10);
  const [busy, setBusy] = useState(false);
  const [opm, setOpm] = useState({});
  const fileRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = () => listFotos().then(d => {
    setFotos(d.fotos || []);
    if (d.max) setMax(d.max);
    const o = {};
    (d.fotos || []).forEach(f => { o[f.id] = f.opmerking || ''; });
    setOpm(o);
  }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      try { await uploadFoto(f, ''); }
      catch (err) { alert(err?.response?.data?.detail || 'Upload mislukt'); break; }
    }
    setBusy(false);
    load();
  };

  const saveOpm = async (fid) => {
    if (!updateOpmerking) return;
    try { await updateOpmerking(fid, opm[fid] || ''); } catch { /* stil */ }
  };

  const del = async (fid) => {
    if (!window.confirm('Deze foto verwijderen?')) return;
    try { await deleteFoto(fid); load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Verwijderen mislukt'); }
  };

  return (
    <div data-testid="lever-fotos-panel">
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="lever-foto-input" />
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy || fotos.length >= max}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#059669] text-white text-sm font-bold disabled:opacity-50"
          data-testid="lever-foto-maken-btn"
        >
          <Camera size={18} weight="bold" /> {busy ? 'Bezig met uploaden…' : 'Foto maken / toevoegen'}
        </button>
        <span className="text-[11px] text-gray-400">{fotos.length}/{max} foto's · bv. schade of staat van de goederen</span>
      </div>
      {fotos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {fotos.map((f, i) => (
            <div key={f.id} className="border border-gray-200 bg-gray-50" data-testid={`lever-foto-${i}`}>
              <img
                src={`${BACKEND}/api/lever-fotos/${f.id}.jpg`}
                alt={`Leveringsfoto ${i + 1}`}
                className="w-full h-32 object-cover cursor-pointer"
                onClick={() => window.open(`${BACKEND}/api/lever-fotos/${f.id}.jpg`, '_blank')}
              />
              <div className="p-1.5 space-y-1">
                <input
                  className="w-full text-[11px] border border-gray-200 px-1.5 py-1"
                  placeholder="Opmerking (bv. beschadigde hoek)"
                  value={opm[f.id] ?? ''}
                  onChange={e => setOpm(p => ({ ...p, [f.id]: e.target.value }))}
                  onBlur={() => saveOpm(f.id)}
                  data-testid={`lever-foto-opm-${i}`}
                />
                <div className="flex justify-between items-center">
                  <span className="text-[9px] text-gray-400">{(f.uploaded_at || '').slice(0, 16).replace('T', ' ')}</span>
                  <button type="button" className="text-red-400 hover:text-red-600" onClick={() => del(f.id)} data-testid={`lever-foto-del-${i}`}>
                    <Trash size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
